import cloneDeep from 'lodash.clonedeep';
import { action, computed, makeObservable, observable } from 'mobx';
import { FormEvent, MouseEvent } from 'react';
import { IMealChoiceEditModal } from '../types/mealChoiceEditModal';
import { LOCALES, defaulLangTextValidation, defaultTTranslate } from '~/constants/main';
import { modalStore } from '~/mobx/modalStore';
import { IMealChoice, IMealItem } from '~/types/combo';
import { ILangTextValidation, TTranslate } from '~/types/main';
import { IProductV4 } from '~/types/product';
import { randomFloatNumber, scrollToErrorElement } from '~/utils/helpers';
import { confirmZeroPrice } from '~/utils/modalConfirmations';
import { productUtils } from '~/utils/productUtils';
import { emptyNameValidation } from '~/utils/validation';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';

const headerHeight: number = 104;
const contentPaddingsHeight: number = 50;
const footerHeight: number = 60;
const saveBtnHeight: number = 60;
const deleteBtnHeight: number = 54;
const mdfExclModalOpenBtnHeight: number = 27;
const nameBlockHeight: number = 78;
const itemBlockHeight: number = 247;
const errorHeight: number = 27;

class MealChoiceEditModalStore {
  constructor() {
    makeObservable(this);
  }

  /** needed for correct operation of saving and deleting */
  @observable private initChoiceId: IMealChoiceEditModal['choiceId'] = undefined;
  private choiceId: IMealChoice['id'] = 0;
  private groupId: IMealChoiceEditModal['groupId'] = 0;
  public sizes: IMealChoiceEditModal['sizes'] = [];
  private onSubmitHandler: IMealChoiceEditModal['onSubmit'] = () => {
    return;
  };
  private onDeleteHandler: IMealChoiceEditModal['onDelete'] = () => {
    return;
  };

  private fullItemsList: IProductV4[] = [];

  @observable public choiceName: TTranslate = defaultTTranslate;
  @observable public items: IMealItem[] = [];

  public get isEditAction(): boolean {
    return Boolean(this.initChoiceId);
  }

  public get modalHeader(): string {
    return this.isEditAction ? 'edit_or_del_cmgc' : 'add_item_choice';
  }

  @computed get modalHeight(): number {
    let height: number = headerHeight + contentPaddingsHeight + nameBlockHeight + footerHeight + saveBtnHeight + this.sizes.length * itemBlockHeight;

    height += this.itemsWithAvailableShowOpenMealItemMdfExclModalLength * mdfExclModalOpenBtnHeight;
    if (this.isEditAction) height += deleteBtnHeight;
    if (!this.isValid) height += errorHeight;

    return height;
  }

  public initStore = (props: IMealChoiceEditModal) => {
    this.initChoiceId = props.choiceId ? props.choiceId : undefined;

    this.choiceId = props.choiceId ? props.choiceId : randomFloatNumber();
    this.groupId = props.groupId;
    this.sizes = props.sizes;

    this.onSubmitHandler = props.onSubmit;
    this.onDeleteHandler = props.onDelete;

    this.choiceName = props.choiceName ? props.choiceName : defaultTTranslate;
    this.items = props.choiceId ? props.items.filter((item) => item.choice_id === props.choiceId) : [];

    this.validation = cloneDeep(defaulLangTextValidation);
  };

  @action public onChangeChoiceName = (name: string, locales: LOCALES) => {
    this.choiceName = { ...this.choiceName, [locales]: name };

    if (!this.isValid) this.setValidation();
  };

  public getItemBySize = (sizeId: number): IMealItem | undefined => {
    return this.items.find((item) => item.size_id === sizeId);
  };

  private get itemsWithAvailableShowOpenMealItemMdfExclModalLength(): number {
    return this.items.filter((item) => this.showOpenMealItemMdfExclModalButton(item)).length;
  }

  public showOpenMealItemMdfExclModalButton = (item: IMealItem | undefined): boolean => {
    if (!item) return false;
    return Boolean(item.is_modifiers);
    // TODO: combo exclusions
    // return Boolean(item.is_modifiers || item.is_exclusions);
  };

  public onSetFullItemsList = (list: IProductV4[]) => {
    this.fullItemsList = list;
  };

  @action public onSelectItem = (options: IPickerOption[], sizeId: number) => {
    if (options.length) {
      const item: IProductV4 | undefined = this.fullItemsList.find((_item) => _item.id === options[0].value);

      if (item) {
        const mealItem: IMealItem = productUtils.transformToMealItem(item, sizeId, this.groupId, this.choiceId);
        const alreadySelected: boolean = this.items.some((_item) => _item.size_id === sizeId);

        this.items = alreadySelected ? this.items.map((_item) => (_item.size_id === sizeId ? mealItem : _item)) : this.items.concat(mealItem);
      }

      return;
    }

    this.items = this.items.filter((_item) => _item.size_id !== sizeId);
  };

  @action public onChangeItemPrice = (sizeId: number, price: string | undefined) => {
    this.items = this.items.map((item) => (item.size_id === sizeId ? { ...item, price: price || '0' } : item));
  };

  public onOpenMealItemMdfExclModal = (item: IMealItem) => {
    modalStore.openMealItemMdfExclModal({ item });
  };

  @action public onSubmit = (event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    this.setValidation();

    if (!this.isValid) {
      scrollToErrorElement();
      return;
    }

    const zeroPrice: boolean = Boolean(this.items.find((item) => Number(item.price) === 0));
    if (zeroPrice) {
      confirmZeroPrice({ onConfirm: this._onSubmit });
      return;
    }

    this._onSubmit();
  };
  private _onSubmit = () => {
    const choice = { id: this.choiceId, name: this.choiceName, group_id: this.groupId };

    this.onSubmitHandler(choice, this.items, this.isEditAction);
    this.onCloseModal();
  };

  @action public onDelete = () => {
    if (this.initChoiceId) this.onDeleteHandler(this.initChoiceId);

    this.onCloseModal();
  };

  private get isValid(): boolean {
    return this.validation.enValid && this.validation.arValid;
  }

  @observable public validation: ILangTextValidation = cloneDeep(defaulLangTextValidation);
  private setValidation = () => {
    this.validation = cloneDeep(defaulLangTextValidation);

    [this.validation] = emptyNameValidation(this.choiceName, this.validation, true);
  };

  @action public onCloseModal = () => {
    modalStore.closeMealChoiceEditModal();
  };
}

export const mealChoiceEditModalStore = new MealChoiceEditModalStore();
